import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { IClient } from "interfaces/Client";
import { IFormError } from "interfaces/ExpressError";
import { ICreateOccurrence, IOccurrence } from "interfaces/Occurrence";
import { IPoliceman } from "interfaces/Policeman";
import { ISuperficialClient } from "interfaces/SubClient";

import { ISuperficialIVehicleProps } from "interfaces/Vehicle";
import { getOccurrencesIdService } from "services/OccurrenceService";
import { FormError } from "./IUserContext";

type ProviderValues = {
  children: ReactNode;
};

type ContextValues = {
  occurrence: Omit<ICreateOccurrence, "superficialVehicle">;
  setOccurrence: React.Dispatch<
    React.SetStateAction<Omit<ICreateOccurrence, "superficialVehicle">>
  >;
  filterClient: IClient;
  setFilterClient: React.Dispatch<React.SetStateAction<IClient>>;
  filterSubClient: string;
  setFilterSubClient: React.Dispatch<React.SetStateAction<string>>;
  filterPoliceman: string;
  setFilterPoliceman: React.Dispatch<React.SetStateAction<string>>;
  searching: boolean;
  vehicle: ISuperficialIVehicleProps;
  errors: FormError;
  setErrors: React.Dispatch<React.SetStateAction<FormError>>;
  isManual: boolean;
  setIsManual: React.Dispatch<React.SetStateAction<boolean>>;
  isSubclientAddressRegister: boolean;
  setIsSubclientAddressRegister: React.Dispatch<React.SetStateAction<boolean>>;
  setVehicle: React.Dispatch<React.SetStateAction<ISuperficialIVehicleProps>>;
  addError: (key: string, message: string) => void;
  removeError: (field: string) => void;
  resetOccurrence: () => void;
  retrieveOccurrence: (occurrence: IOccurrence) => void;
  setSelectedPolicemen:React.Dispatch<React.SetStateAction<IPoliceman[]>>
  selectedPolicemen:IPoliceman[]
};

export const OccurrenceRegisterContext = createContext({} as ContextValues);

export function OccurrenceRegisterProvider({ children }: ProviderValues) {
  const [occurrence, setOccurrence] = useState<
    Omit<ICreateOccurrence, "superficialVehicle">
  >({ workType: "Policial" } as ICreateOccurrence);

  const [vehicle, setVehicle] = useState<ISuperficialIVehicleProps>(
    {} as ISuperficialIVehicleProps
  );
  const [filterClient, setFilterClient] = useState({} as IClient);
  const [filterSubClient, setFilterSubClient] = useState("");
  const [filterPoliceman, setFilterPoliceman] = useState("3");
  const [errors, setErrors] = useState<IFormError>({});
  const [isManual, setIsManual] = useState<boolean>(false);
  const [searching, setSearching] = useState<boolean>(false);
  const [selectedPolicemen, setSelectedPolicemen] = useState<IPoliceman[]>([]);

  const [isSubclientAddressRegister, setIsSubclientAddressRegister] = useState<boolean>(true);

  async function retrieveOccurrence(occurrenceData: IOccurrence) {
    setSearching(true);
    return await getOccurrencesIdService(occurrenceData.id)
      .then((res) => {
        const _occurrence = { ...res.item };
        setOccurrence({
          policemen:[],
          workType: _occurrence.workType,
          isManual,
          city: _occurrence.occurrencesSuperficialAddresses.superficialAddress
            .city,
          district:
            _occurrence.occurrencesSuperficialAddresses.superficialAddress
              .district,
          number:
            _occurrence.occurrencesSuperficialAddresses.superficialAddress
              .number,
          patrimonialType: _occurrence.patrimonialType,
          postalCode:
            _occurrence.occurrencesSuperficialAddresses.superficialAddress
              .postalCode,
          street:
            _occurrence.occurrencesSuperficialAddresses.superficialAddress
              .street,
          uf: _occurrence.occurrencesSuperficialAddresses.superficialAddress.uf,
          coordX:
            _occurrence.occurrencesSuperficialAddresses.superficialAddress
              .coordX,
          coordY:
            _occurrence.occurrencesSuperficialAddresses.superficialAddress
              .coordY,
          id: _occurrence.id,
          lastCoordX: _occurrence.lastCoordX,
          lastCoordY: _occurrence.lastCoordY,
          lastCoords: _occurrence.stringLastCoords
            ? JSON.parse(_occurrence.stringLastCoords)
            : [],
          numberBO: _occurrence.numberBO,
          reference:
            _occurrence.occurrencesSuperficialAddresses.superficialAddress
              .reference,
          // superficialClient: _occurrence.superficialClient,
          Client: _occurrence.client,
        });
        setFilterClient({
          ..._occurrence.client,
        });
        setVehicle({
          brand: _occurrence.superficialVehicle.brand,
          color: _occurrence.superficialVehicle.color,
          description: _occurrence.superficialVehicle.description,
          model: _occurrence.superficialVehicle.model,
          plate: _occurrence.superficialVehicle.plate,
          // superficialClient: _occurrence.superficialVehicle.superficialClient,
          id: _occurrence.superficialVehicle.id,
          type: _occurrence.superficialVehicle.type,
          occurrence: _occurrence,
        });
        setSearching(false);
      })
      .catch((err) => {
        setSearching(false);
        console.log(err);
      });
  }

  function addError(key: string, message: string) {
    setErrors((prev) => ({
      ...prev,
      [key]: message
    }))
  }

  function removeError(field: string) {
    const _errors = { ...errors };
    delete _errors[field];

    setErrors(() => {
      return _errors;
    });
  }

  function resetOccurrence() {
    setSearching(true);
    setOccurrence({ workType: "Policial" } as ICreateOccurrence);
    setVehicle({} as ISuperficialIVehicleProps);
    setFilterClient({} as IClient);
    setErrors({});
    setSearching(false);
  }

  return (
    <OccurrenceRegisterContext.Provider
      value={{
        occurrence,
        setOccurrence,
        vehicle,
        setVehicle,
        errors,
        setErrors,
        isManual,
        setIsManual,
        removeError,
        addError,
        resetOccurrence,
        retrieveOccurrence,
        searching,
        filterClient,
        setFilterClient,
        filterSubClient,
        setFilterSubClient,
        filterPoliceman,
        setFilterPoliceman,
        isSubclientAddressRegister,
        setIsSubclientAddressRegister,
        setSelectedPolicemen,
        selectedPolicemen
      }}
    >
      {children}
    </OccurrenceRegisterContext.Provider>
  );
}

export const useOccurrenceRegister = () => {
  return useContext(OccurrenceRegisterContext);
};
